import type {
  CountryCode,
  Intervention,
  OtherInterventions,
  SampleTreeRegistration,
  SingleTreeRegistration,
  TreeProjectClassification,
} from '@planet-sdk/common';
import type { MapGeoJSONFeature, PointLike } from 'react-map-gl-v7/maplibre';
import type { Position } from 'geojson';
import type { ParsedUrlQuery } from 'querystring';
import type {
  MapProjectProperties,
  ExtendedProject,
  MapProject,
} from '../features/common/types/projectv2';
import type {
  MapLibreRef,
  ProjectSiteFeatureCollection,
  ProjectSiteFeature,
} from '../features/common/types/map';

import centroid from '@turf/centroid';

type MetaDataValue = {
  value: string;
  label: string;
};

type FormattedMetadataEntry = {
  key: string;
  value: string;
};

export type MobileOs = 'android' | 'ios' | undefined;

const paramsToDelete = ['ploc', 'backNavigationUrl', 'site'];
const nonDisplayPublicMetadataKeys = ['isEntireSite'];

export const MAIN_MAP_LAYERS = {
  SATELLITE_LAYER: 'satellite-layer',
  PLANT_POLYGON: 'plant-polygon-layer',
  PLANT_POINT: 'point-layer',
  SITE_POLYGON: 'site-polygon-fill-layer',
  SITE_POLYGON_LINE: 'site-polygon-line-layer',
  SELECTED_LINE: 'line-selected',
  DATE_DIFF_LABEL: 'datediff-label',
};

export const PLANT_LAYERS = [
  MAIN_MAP_LAYERS.PLANT_POLYGON,
  MAIN_MAP_LAYERS.PLANT_POINT,
];

export const INTERACTIVE_LAYERS = [
  MAIN_MAP_LAYERS.PLANT_POLYGON,
  MAIN_MAP_LAYERS.PLANT_POINT,
  MAIN_MAP_LAYERS.SITE_POLYGON,
];

export const MAIN_MAP_ANIMATION_DURATIONS = {
  ZOOM_OUT: 1600,
  ZOOM_IN: 4000,
} as const;

type RouteParams = {
  siteId?: string | null;
  plocId?: string | null;
};

/** Type predicate to check that the property contains a string value*/
const isStringValue = (entry: [string, unknown]): entry is [string, string] => {
  return typeof entry[1] === 'string';
};

/**
 * Updates and returns a query object for a URL based on the current path and specified parameters.
 * It preserves selected query parameters, removes unwanted ones, and adds or updates the site parameter.
 * @param asPath
 * @param query
 * @param siteId
 * @returns An updated query object with preserved, removed, and new parameter
 */

export const buildProjectDetailsQuery = (
  query: ParsedUrlQuery,
  routeParams: RouteParams
): Record<string, string> => {
  // Convert ParsedUrlQuery to Record<string, string> by filtering out non-string values
  const currentQuery: Record<string, string> = Object.fromEntries(
    Object.entries(query).filter(isStringValue)
  );

  // Preserve and delete query parameters
  paramsToDelete.forEach((param) => delete currentQuery[param]);

  // Add routing params if provided
  if (routeParams.siteId) {
    currentQuery.site = routeParams.siteId;
  }

  if (routeParams.plocId) {
    currentQuery.ploc = routeParams.plocId;
  }

  return currentQuery;
};

/**
 * Determines the category of a project based on its properties.
 * @param {MapProjectProperties | ExtendedProject} projectProperties - The project properties to evaluate.
 * @returns {string} - Returns the category of the project as 'topProject', 'regularProject', or 'nonDonatableProject'.
 */

export const getProjectCategory = (
  projectProperties: MapProjectProperties | ExtendedProject
) => {
  if (
    projectProperties.purpose === 'trees' &&
    projectProperties.isTopProject &&
    projectProperties.isApproved
  ) {
    return 'topProject';
  } else if (projectProperties.allowDonations) {
    return 'regularProject';
  } else {
    return 'nonDonatableProject';
  }
};

export const availableFilters: TreeProjectClassification[] = [
  'large-scale-planting',
  'agroforestry',
  'natural-regeneration',
  'managed-regeneration',
  'urban-planting',
  'mangroves',
  'other-planting',
];

export const isValidClassification = (
  value: string
): value is TreeProjectClassification => {
  return availableFilters.includes(value as TreeProjectClassification);
};

/**
 * Returns all rendered features at the given point on the map, limited to specific layers.
 *
 * Important:
 * - The order of features in the returned array reflects the visual stacking order of layers.
 * - The topmost (last drawn) layer in the style stack will appear first in the array.
 *
 * Example:
 * If the layers are added in this order:
 *   1. "site-polygon-fill-layer" (bottom)
 *   2. "plant-polygon-layer" (middle)
 *   3. "point-layer" (top)
 *
 * Then:
 *   - If all 3 features overlap at the click point:
 *       features[0] => point-layer feature (topmost)
 *       features[1] => plant-polygon-layer feature
 *       features[2] => site-polygon-fill-layer feature (bottommost)
 *   - If only site and plant polygons are under the point:
 *       features[0] => plant-polygon-layer feature
 *       features[1] => site-polygon-fill-layer feature
 *
 * Use this order to determine which feature the user is most likely interacting with visually.
 *
 * @param mapRef - Ref to the MapLibre map instance
 * @param point - The screen coordinate (e.point) to query
 * @returns An array of features under the point, or undefined if the map is not ready. Returns an empty array if no features are found.
 */

export function getFeaturesAtPoint(
  mapRef: MapLibreRef,
  point: PointLike
): MapGeoJSONFeature[] | undefined {
  if (!mapRef.current) return;
  const map = mapRef.current.getMap();
  const canvas = map.getCanvas();

  const availableLayers = INTERACTIVE_LAYERS.filter((layerId) =>
    map.getLayer(layerId)
  );

  if (availableLayers.length === 0) {
    canvas.style.cursor = '';
    return [];
  }

  const features = map.queryRenderedFeatures(point, {
    layers: availableLayers,
  });

  if (features.length === 0) {
    canvas.style.cursor = '';
    return [];
  }

  canvas.style.cursor = 'pointer';
  return features;
}

/**
 * Finds the index of the site in the sites array that matches the feature under the cursor.
 *
 * This is used to detect which site polygon the user interacted with.
 *
 * @param sites - The array of site GeoJSON features (polygons or multipolygons)
 * @param features - The array of rendered features returned by queryRenderedFeatures
 * @returns The index of the matching site in the `sites` array, or -1 if not found
 */

export const getSiteIndex = (
  sites: ProjectSiteFeature[],
  features: MapGeoJSONFeature[]
) => {
  const siteFeature = features.find(
    (f) => f.layer.id === MAIN_MAP_LAYERS.SITE_POLYGON
  );
  if (!siteFeature) return -1;

  return sites.findIndex(
    (site) => site.properties.id === siteFeature.properties.id
  );
};

/**
 * Determines whether a map feature belongs to a plant intervention layer.
 *
 * This is used to distinguish plant-related map features from other
 * interactive layers such as site polygons or background layers.
 *
 * @param feature - A GeoJSON feature from the map
 * @returns `true` if the feature is from a plant layer, otherwise `false`
 */
export const isPlantFeature = (feature: MapGeoJSONFeature): boolean => {
  return PLANT_LAYERS.includes(feature.layer.id);
};

/**
 * Retrieves the intervention that matches a given map feature.
 *
 * The function:
 * 1. Ensures the interventions list is available and non-empty
 * 2. Compares the feature's `properties.id` with intervention IDs
 * 3. Returns the first matching intervention, if found
 *
 * @param interventions - List of available interventions (may be null)
 * @param feature - A GeoJSON feature from a map interaction event
 * @returns The matched intervention, or `undefined` if no match exists
 */
export const getInterventionInfo = (
  interventions: Intervention[] | null,
  feature: MapGeoJSONFeature
): Intervention | undefined => {
  if (!interventions?.length) return;

  return interventions.find(
    (intervention) => intervention.id === feature.properties?.id
  );
};

export const formatHid = (hid: string | undefined) => {
  return hid ? hid.slice(0, 3) + '-' + hid.slice(3) : null;
};

export const getActiveSingleTree = (
  selected: Intervention | null,
  hovered: Intervention | null,
  selectedSample: SampleTreeRegistration | null
): SingleTreeRegistration | SampleTreeRegistration | undefined => {
  if (selected?.type === 'single-tree-registration') return selected;
  if (hovered?.type === 'single-tree-registration') return hovered;
  if (selectedSample?.type === 'sample-tree-registration')
    return selectedSample;
  return undefined;
};

const isValidCoordinate = (coord: Position) =>
  Array.isArray(coord) &&
  coord.length === 2 &&
  typeof coord[0] === 'number' &&
  typeof coord[1] === 'number';

/**
 * Filters a list of project features to include only those with valid coordinates.
 * A project feature is considered valid if its geometry.
 * coordinates array contains exactly two numbers (longitude and latitude)
 * @param {MapProject[]} projects
 * @returns
 */
export const getValidFeatures = (projects: MapProject[]) =>
  projects?.filter((feature) =>
    isValidCoordinate(feature.geometry.coordinates)
  ) ?? [];

/**
 * Calculates the centroid (geometric center) of a collection of project features.
 * The features are converted into a GeoJSON FeatureCollection, and the centroid is calculated using Turf.js.
 * @param {MapProject[]} features
 * @returns
 */
export const calculateCentroid = (features: MapProject[]) => {
  const featureCollection = {
    type: 'FeatureCollection',
    features,
  };
  return centroid(featureCollection);
};

/**
 *  Centers the map on a given longitude and latitude using a smooth transition (animated).
 *  It uses the MapLibre easeTo method for map animation with a custom easing function
 * @param mapRef
 * @param param1
 * @returns
 */
export const centerMapOnCoordinates = (
  mapRef: MapLibreRef,
  [longitude, latitude]: Position
) => {
  if (!mapRef.current) return;
  mapRef.current.getMap().easeTo({
    center: [longitude, latitude],
    duration: 1200,
    easing: (t) => t * (2 - t),
  });
};

export const generateProjectLink = (
  projectGuid: string,
  routerAsPath: string //e.g. /en/yucatan, /en
) => {
  // just use routerAsPath as-is, let localizedPath handle locale stripping
  return `/${projectGuid}?backNavigationUrl=${encodeURIComponent(
    routerAsPath
  )}`;
};

/**
 * Compares the coordinates of the map center with the centroid coordinates to determine if they are approximately equal.
 *
 * @param mapCenter - The center coordinates of the map, containing longitude (`lng`) and latitude (`lat`). Can be `undefined`.
 * @param centroidCoords - The centroid coordinates as a tuple containing longitude and latitude.
 * @param epsilon - The threshold for determining equality, representing a very small difference (default is 0.00009, which corresponds to approximately 10 meters at the Equator).
 * @returns `true` if the coordinates are approximately equal within the specified threshold, otherwise `false`.
 */
export const areMapCoordsEqual = (
  mapCenter: { lng: number; lat: number } | undefined,
  centroidCoords: Position,
  epsilon = 0.00009 // Very small difference threshold corresponding to 10m at Equator
): boolean => {
  if (mapCenter === undefined) return false;
  return (
    Math.abs(mapCenter.lng - centroidCoords[0]) < epsilon &&
    Math.abs(mapCenter.lat - centroidCoords[1]) < epsilon
  );
};

export const getDeviceType = (): MobileOs => {
  const userAgent = navigator.userAgent;
  if (/android/i.test(userAgent)) return 'android';
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
  return undefined;
};

/**
 * Checks if the "Firealert Fires" feature is enabled via env variable or query string.
 * @returns boolean, Whether this feature is enabled or not
 */
export function isFirealertFiresEnabled() {
  const isEnvVariableEnabled =
    process.env.NEXT_PUBLIC_ENABLE_FIREALERT_FIRES?.trim().toLowerCase() ===
    'true';
  const isQueryStringEnabled =
    new URLSearchParams(window.location.search)
      .get('fa-fires')
      ?.toLowerCase() === 'true';

  return isEnvVariableEnabled || isQueryStringEnabled;
}

/**
 * Returns a GeoJSON FeatureCollection from a list of site features.
 * Filters out features without valid geometry.
 *
 * @param sites - Array of project site features with geometry.
 * @returns GeoJSON FeatureCollection with valid features only.
 */
export function getSitesGeoJson(
  sites: ProjectSiteFeature[]
): ProjectSiteFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: sites.filter((site) => !!site.geometry),
  };
}

/**
 * Safely parses a JSON string into a JavaScript value without throwing errors.
 * @param {string} str - The JSON string to parse.
 * @returns {unknown} The parsed value if valid JSON, otherwise `null` to avoid throwing errors.
 */
function tryParseJson(str: string): unknown {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

/**
 * Type guard that checks if a value is a non-array object
 * with string `label` and `value` properties.
 */
function isMetaDataValue(obj: unknown): obj is MetaDataValue {
  return (
    !!obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    'label' in obj &&
    'value' in obj &&
    typeof (obj as { value: unknown }).value === 'string' &&
    typeof (obj as { label: unknown }).label === 'string'
  );
}

/** Type guard that checks if a value is a non-array object containing a string `value` property. */
function isObjectWithStringValue(obj: unknown): obj is { value: string } {
  return (
    !!obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    'value' in obj &&
    typeof (obj as { value: unknown }).value === 'string'
  );
}

/**
 * Formats a single metadata entry into a user-facing key-value pair.
 *
 * Rules:
 * - Skips entries whose keys are in `nonDisplayPublicMetadataKeys`.
 * - Accepts direct string values.
 * - Accepts objects with `value` and `label` properties.
 * - If `value` is a JSON string, attempts to parse it and extract a nested string `value`.
 * @param {string} metaKey - The metadata property name.
 * @param {unknown} metaValue - The metadata property value.
 * @returns {{ key: string; value: string } | null} The formatted metadata entry, or `null` if not displayable.
 */
function formatMetadataEntry(
  metaKey: string,
  metaValue: unknown
): FormattedMetadataEntry | null {
  if (nonDisplayPublicMetadataKeys.includes(metaKey)) return null;

  if (typeof metaValue === 'string') {
    return { key: metaKey, value: metaValue };
  }

  if (!isMetaDataValue(metaValue)) return null;

  let finalValue = metaValue.value;
  const parsedValue = tryParseJson(metaValue.value);
  if (isObjectWithStringValue(parsedValue)) {
    finalValue = parsedValue.value;
  }

  return {
    key: metaValue.label,
    value: finalValue,
  };
}

/**
 * Checks if metadata is a processable object (not null, not array, is object).
 * @param {unknown} metadata - The value to check.
 * @returns {boolean} true if `metadata` is a non-array object.
 */
function isProcessableMetadata(metadata: unknown): metadata is object {
  return !(
    !metadata ||
    typeof metadata !== 'object' ||
    Array.isArray(metadata)
  );
}

/**
 * Extracts and formats the `metadata.public` section of an intervention info
 * into an array of display-friendly key-value pairs.
 * @param {OtherInterventions} interventionInfo - The intervention object containing public metadata.
 * @returns {{ key: string; value: string }[]} - Array of displayable metadata entries.
 */
export function prepareInterventionMetadata(
  interventionInfo: OtherInterventions
): FormattedMetadataEntry[] {
  const publicMetadata = interventionInfo.metadata?.public;

  if (!isProcessableMetadata(publicMetadata)) return [];

  return Object.entries(publicMetadata)
    .map(([key, value]) => formatMetadataEntry(key, value))
    .filter((entry): entry is { key: string; value: string } => !!entry);
}

/**
 * Returns top projects with purpose "trees".
 */

export const getTopProjects = (projects: MapProject[] | null): MapProject[] => {
  if (!projects) return [];

  return projects.filter(
    (project) =>
      project.properties.purpose === 'trees' &&
      project.properties.isTopProject === true
  );
};

/**
 * Filters projects that allow donations.
 *
 * @param projects - List of projects to filter
 * @returns Projects where donations are enabled
 */

export const filterByDonation = (projects: MapProject[]) => {
  return projects.filter((project) => project.properties.allowDonations);
};

/**
 * Filters projects by selected tree classifications.
 *
 * - If no classification is selected, the original list is returned.
 * - Only applies to projects with purpose "trees".
 *
 * @param projects - List of projects to filter
 * @param selectedClassification - Selected tree classifications
 * @returns Filtered projects matching the selected classifications
 */

export const filterByClassification = (
  projects: MapProject[],
  selectedClassification: TreeProjectClassification[]
) => {
  if (selectedClassification.length === 0) return projects;
  return projects.filter((project) => {
    if (project.properties.purpose === 'trees')
      return selectedClassification.includes(project.properties.classification);
  });
};

// Stroke / bar / ligature letters that Unicode NFD does NOT decompose, so the
// combining-mark strip below can't fold them. Mapped explicitly so search is
// accent-insensitive for these too (e.g. Łódź → lodz, Straße → strasse).
const LATIN_FOLD: Record<string, string> = {
  ø: 'o',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  þ: 'th',
  ı: 'i',
};

// Derived from LATIN_FOLD so the two can never drift out of sync.
const LATIN_FOLD_REGEX = new RegExp(
  `[${Object.keys(LATIN_FOLD).join('')}]`,
  'g'
);

// Normalize a string for project search: strip diacritics (NFD + combining-mark
// removal), fold the stroke/ligature letters NFD leaves behind, lowercase, and
// treat a hyphen as a space (collapsing repeated separators). This makes search
// insensitive to accents and hyphens: "Plant for Ghana" matches "Plant-for-Ghana"
// and "Yucatan" matches "Yucatán".
const normalizeForSearch = (text: string | undefined | null): string =>
  text
    ? text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(LATIN_FOLD_REGEX, (c) => LATIN_FOLD[c] ?? c)
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

// German writes umlauts as digraphs too (ü→ue, ö→oe, ä→ae). Expanding them gives a second normalized form so all spellings cross-match: "München", "Munchen" (accent-stripped) and "Muenchen" (digraph) all find the same project. Only an additional match path, so it never removes matches for other languages.
const expandGermanDigraphs = (text: string): string =>
  text.replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe').replace(/[üÜ]/g, 'ue');

const normalizeDigraph = (text: string | undefined | null): string =>
  text ? normalizeForSearch(expandGermanDigraphs(text)) : '';

/**
 * Filters projects based on a search keyword.
 *
 * The search is case-insensitive and tolerant of common text variations,
 * including accents/diacritics, German umlaut spellings (e.g. München ↔
 * Muenchen), and hyphens (e.g. Plant-for-Ghana ↔ Plant for Ghana).
 *
 * Matches against:
 * - Project name
 * - Project location (only for tree projects)
 * - TPO name
 * - Localized country name
 *
 * @param projects - List of projects to filter
 * @param keyword - User-entered search keyword
 * @param getCountryLabel - Function that resolves a country code to a localized country name
 * @returns Projects matching the search keyword
 */

export const filterBySearch = (
  projects: MapProject[] | null,
  keyword: string,
  getCountryLabel: (code: CountryCode) => string
) => {
  if (!keyword?.trim()) {
    return [];
  }
  const keywordBase = normalizeForSearch(keyword);
  if (!keywordBase) {
    return [];
  }
  const keywordDigraph = normalizeDigraph(keyword);

  // A field matches if the keyword is found in either its accent-stripped or its German-digraph-expanded form (see normalizeDigraph).
  const fieldMatches = (text: string | undefined | null): boolean =>
    normalizeForSearch(text).includes(keywordBase) ||
    normalizeDigraph(text).includes(keywordDigraph);

  const filteredProjects = projects?.filter((project: MapProject) => {
    const location =
      project.properties.purpose === 'trees' ? project.properties.location : '';
    const country = getCountryLabel(project.properties.country);

    return (
      fieldMatches(project.properties.name) ||
      fieldMatches(location) ||
      fieldMatches(project.properties.tpo.name) ||
      fieldMatches(country)
    );
  });
  return filteredProjects;
};

/**
 * Resolves a site ID from the provided site index.
 *
 * @param sites - List of project site features
 * @param siteIndex - Index of the selected site
 * @returns The site ID if the index is valid and sites exist, otherwise `null`
 */
export const getSiteIdFromIndex = (
  sites: ProjectSiteFeature[],
  siteIndex: number | null
): string | null => {
  return sites && sites.length > 0 && siteIndex !== null
    ? sites[siteIndex]?.properties.id
    : null;
};

export const FIRST_SITE_INDEX = 0;

/**
 * Returns `true` if a project has no selectable sites.
 *
 * A project has no selectable sites when the site list is empty or every site
 * has `geometry === null`.
 *
 * @param sites - Project site features
 * @returns Whether at least one site has valid geometry
 */
export const hasNoSites = (
  sites: ProjectSiteFeature[] | null | undefined
): boolean => {
  if (!sites || sites.length === 0) return true;
  return sites.every((site) => site.geometry === null);
};

/**
 * Checks whether the provided value is a string.
 *
 * Acts as a type guard to safely narrow an `unknown` value to `string`.
 *
 * @param value - The value to check
 * @returns `true` if the value is a string, otherwise `false`
 */
export const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};
